import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, Inject, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subscription, filter, tap } from 'rxjs';

import { Location } from '@shared/location/models';
import { Messages } from '@shared/restaurant/models';
import { Account } from '@shared/account/models';
import {
  ValidationService,
  requiredConditionalValidator,
  bitlyValidator,
} from '@tablesready/common-ui';
import { DYNAMICFIELDS, DynamicField } from '@tablesready/common-ui';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AuthService } from '@tablesready/frontend-auth';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import {
  WhoService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';
import { POSITION_IN_LINE_OPTIONS } from './notifications.constant';

@Component({
  selector: 'tablesready-notifications',
  styleUrls: ['./notifications.component.scss'],
  templateUrl: './notifications.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsComponent implements OnInit, OnDestroy {
  location: Location;
  account: Account;
  dynamicFieldOptions: DynamicField[];
  completedTimeOptions: string[];
  positionInLineOptions: any;
  smsChars: number;
  voiceChars = 200;
  maxNonAnsiChars: number = 70;
  maxAnsiChars: number;
  isMobile = false;
  isLocationOnBusinessPlan = false;

  notificationsForm: UntypedFormGroup;

  private subscriptions = new Subscription();

  constructor(
    private readonly authService: AuthService,
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly fb: UntypedFormBuilder,
    private readonly validationService: ValidationService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly breakPointObserver: BreakpointObserver,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.maxAnsiChars = Number(this.appVariables.WHITELABEL_SMS_CHARS);
    this.maxNonAnsiChars = Number(this.appVariables.WHITELABEL_SMS_NON_ANSI_CHARS);
  }

  ngOnInit(): void {
    this.completedTimeOptions = this.getCompletedTimeOutOptions();
    this.positionInLineOptions = POSITION_IN_LINE_OPTIONS;

    const authUserSub = this.authRestaurantService.select()
      .pipe(
        filter(user => user.location !== undefined),
        tap(({location, account}) => {
          this.location = location;
          this.account = account;

          this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);
          this.setDynamicFields();
          this.smsChars = 200 > this.maxAnsiChars ? 200 : this.maxAnsiChars;
          this.notificationsForm = this.fb.group({
            useWhatsApp: [false],
            smsPageMessage: [null, [
              Validators.required,
              bitlyValidator,
              this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
            ]],
            smsReply: this.fb.group({
              enabled: [null],
              message: [null, [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]]
            }),
            smsConfirmation: this.fb.group({
              enabled: [null],
              message: [null , [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
            }),
            smsCompleted: this.fb.group({
              enabled: [null],
              message: [null, [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
              timeValue: [null, Validators.required],
              timeFormat: [null, Validators.required]
            }),
            smsNext: this.fb.group({
              enabled: [null],
              message: [null, [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
              position: [null, Validators.required]
            }),
            smsReminder: this.fb.group({
              enabled: [null],
              message: [null, [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.bookingOptions.onlineReservationsEnabled, this.maxAnsiChars, this.maxNonAnsiChars, 'booking'),
              ]],
              timeValue: [null, Validators.required],
              timeFormat: [null, Validators.required]
            }),
            smsResConfirmation: this.fb.group({
              enabled: [null],
              message: ['', [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.bookingOptions.onlineReservationsEnabled, this.maxAnsiChars, this.maxNonAnsiChars, 'booking'),
              ]]
            }),
            smsReservationCancellation: this.fb.group({
              enabled: [null],
              message: ['', [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.bookingOptions.onlineReservationsEnabled, this.maxAnsiChars, this.maxNonAnsiChars, 'booking'),
              ]],
            }),
            smsWaitlistCancellation: this.fb.group({
              enabled: [null],
              message: ['', [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
            }),
            smsCheckinConfirmation: this.fb.group({
              enabled: [null],
              message: ['' , [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'booking')]],
            }),
            voicePageNotification: this.fb.group({
              enabled: [null],
              message: ['', [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
              ]],
            }),
            toGoOrderConfirmation: this.fb.group({
              enabled: [null],
              message: [null , [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
            }),
            toGoOrderReady: this.fb.group({
              enabled: [null],
              message: [null , [
                requiredConditionalValidator(control => control?.parent?.get('enabled').value),
                bitlyValidator,
                this.validationService.textWatcherValidator(location.publicWaitlist.view, this.maxAnsiChars, this.maxNonAnsiChars, 'waitlist'),
              ]],
            }),
          });
          this.notificationsForm.patchValue(this.getDefaultMessages(location));
          this.enableDisableMessageInputs();
          this.cdr.detectChanges();
        }),
      ).subscribe();
    this.subscriptions.add(authUserSub);

    const smsConfirmationSubscription = this.notificationsForm.get('smsConfirmation.enabled').valueChanges
      .pipe(
        tap(value => {
          if (value && this.location.integrations?.reserveWithGoogle?.enabled) {
            this.notificationsForm.get('smsConfirmation.enabled').disable({onlySelf: true, emitEvent: false});
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(smsConfirmationSubscription);

    const smsWaitlistCancellationSubscription = this.notificationsForm.get('smsWaitlistCancellation.enabled').valueChanges
      .pipe(
        tap(value => {
          if (value && this.location.integrations?.reserveWithGoogle?.enabled) {
            this.notificationsForm.get('smsWaitlistCancellation.enabled').disable({onlySelf: true, emitEvent: false});
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(smsWaitlistCancellationSubscription);

    const mobileSub = this.breakPointObserver.observe(Breakpoints.Handset)
      .pipe(tap(({ matches }) => this.isMobile = matches)).subscribe();
    this.subscriptions.add(mobileSub)
  }

  freePlanConfirm(): void {
    this.pricePlansRestrictionsModalService.display(this.authRestaurantService.get()).subscribe();
  }

  toggleTextarea(event: boolean, property: keyof Messages): void {
    event ? this.notificationsForm.controls[property].get('message').enable() : this.notificationsForm.controls[property].get('message').disable();
  }

  getCompletedTimeOutOptions() {
    let completedTimeOutOptions = [];
    for (let i = 1; i <= 90; i++) { // eslint-disable-line no-magic-numbers
      completedTimeOutOptions.push(i.toString());
    }
    return completedTimeOutOptions;
  }

  getDefaultMessages(user: Location) {
    return {...user.messages} || {
      useWhatsApp: user.messages.useWhatsApp || false,
      smsPageMessage: user.messages.smsPageMessage,
      smsReply: {
        enabled: true,
        message: user.messages.smsReply.message || 'Thank you for your response. Your message will be displayed to the host.',
      },
      smsConfirmation: {
        enabled: false,
        message: user.publicWaitlist && user.publicWaitlist.view ? `You've been added to the ${user.name} waitlist. Check your place in line here: {link}. Reply 'N' to cancel.` : `You've been added to the ${user.name} waitlist. We'll text you when we're ready for you. Reply 'N' to cancel.`,
      },
      smsCompleted: {
        enabled: false,
        message: `Thanks for coming to ${user.name}! Check us out on Facebook to keep on top of the latest specials and events.`,
        timeValue: 3,
        timeFormat: 'hours',
      },
      smsNext: {
        enabled: false,
        message: `It's almost your turn at ${user.name}! Please start heading back. If you've decided to change your plans, please reply 'N' to cancel.'`,
        position: 2,
      },
      smsReminder: {
        enabled: false,
        message: `Your upcoming ${user.displayOptions.labels.reservation} at ${user.name} is on {date}. Reply 'N' to cancel.`,
        timeValue: 1,
        timeFormat: 'days',
      },
      smsResConfirmation: {
        enabled: false,
        message: `Thank you! Your ${user.displayOptions.labels.reservation} is confirmed at ${user.name} on {date}. Reply 'N' to cancel.`,
      },
      smsReservationCancellation: {
        enabled: false,
        message: `Your {reservation} for {name} on {date} has been canceled.`,
      },
      smsWaitlistCancellation: user.messages.smsWaitlistCancellation || {
        enabled: false,
        message: `You have been removed from the {business} waitlist.`,
      },
      voicePageNotification: {
        enabled: true,
        message: `We're ready for you!`,
      },
      smsCheckinConfirmation: {
        enabled: false,
        message: `Thank you! You are checked in. We'll text you when it's your turn.`,
      },
      toGoOrderConfirmation: {
        enabled: false,
        message: `Thank you for your carryout order at {business}! We’ll text you when it’s ready.`,
      },
      toGoOrderReady: {
        enabled: false,
        message: `Your carryout order is ready at {business}! Please come to the host stand to pick it up. Enjoy!`,
      },
    };
  }

  submit() {
    this.notificationsForm.markAsPristine();
    for (const property in this.notificationsForm.getRawValue()) {
      const control = this.notificationsForm.controls[property];
      if(control.get('message')) {
        control.get('message').enable();
      }
    }
    this.whoService.updateMessages(this.notificationsForm.getRawValue()).subscribe(
      (response) => {
        console.log({ response });
        this.alertsService.add({
          type: IAlertTypes.success,
          heading: 'Updated:',
          body: response
        });
      },
      (error) => {
        let message = '';
        if (error instanceof HttpErrorResponse) {
          switch (error.status) {
            case 400:
              message = error.error.error;
              break;
            default:
              message = error.statusText;
          }
        } else {
          message = error.message;
        }

        this.alertsService.add({
          type: IAlertTypes.danger,
          heading: 'Error:',
          body: message,
        });
      }
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setDynamicFields() {
    this.dynamicFieldOptions = DYNAMICFIELDS;
    this.dynamicFieldOptions.forEach(dynamicField => {
      let tooltip = dynamicField.toolTip;
      const links = tooltip.match(/\{([^}]+)\}/g) || [];
      for (const link of links) {
        switch (link) {
          case '{reservation}':
          tooltip = tooltip.replace(link, this.location?.displayOptions?.labels?.reservation || 'reservation');
          break;

        default:
          break;
        }
      }

      dynamicField.toolTip = tooltip;
    });
  }

  enableDisableMessageInputs() {
    for (const property in this.notificationsForm.value) {
      const control = this.notificationsForm.controls[property];
      if(control.get('message')) {
        control.get('enabled').value ? control.get('message').enable() : control.get('message').disable();
      }
    }

    if (this.location.integrations?.reserveWithGoogle?.enabled) {
      if (this.notificationsForm.get('smsConfirmation.enabled').value) {
        this.notificationsForm.get('smsConfirmation.enabled').disable({onlySelf: true, emitEvent: false});
      }
      if (this.notificationsForm.get('smsWaitlistCancellation.enabled').value) {
        this.notificationsForm.get('smsWaitlistCancellation.enabled').disable({onlySelf: true, emitEvent: false});
      }
    } else {
      this.notificationsForm.get('smsConfirmation.enabled').enable({onlySelf: true, emitEvent: false});
      this.notificationsForm.get('smsWaitlistCancellation.enabled').enable({onlySelf: true, emitEvent: false});
    }
  }
}
